import cronstrue from 'cronstrue';

/**
 * Format frequency string for display, handling raw cron expressions
 * and numeric values with fallback to human-readable text.
 */
export const formatFrequency = (frequency: string | undefined | null): string => {
  if (!frequency) return 'N/A';

  if (frequency === 'unscheduled') {
    return 'on demand';
  }
  
  // If already human-readable (doesn't contain cron characters), return as-is
  if (!frequency.match(/^[\d\s*/, -]+$/)) {
    return frequency;
  }
  
  // Handle raw cron strings or numeric values
  try {
    return cronstrue.toString(frequency, { use24HourTimeFormat: false });
  } catch (error) {
    // Fallback for invalid cron expressions
    console.warn('Could not parse frequency:', frequency, error);
    return 'custom schedule';
  }
};

/**
 * Humanize cron expression with the original cron shown in parentheses.
 * Used primarily in admin/scheduling views.
 */
export const humanizeCron = (cronExpr: string | undefined | null): string => {
  if (!cronExpr) return 'N/A';
  
  // Check if it's already human-readable (doesn't contain typical cron characters)
  if (!cronExpr.match(/^[\d\s*/, -]+$/)) {
    return cronExpr;
  }
  
  try {
    const human = cronstrue.toString(cronExpr, { use24HourTimeFormat: false });
    return `${human} (${cronExpr})`;
  } catch (error) {
    console.error('Error parsing cron expression:', error);
    return cronExpr;
  }
};

/**
 * Get badge color based on frequency type.
 */
export const getFrequencyColor = (
  frequency: string | undefined | null
): 'blue' | 'gray' => {
  if (!frequency || frequency === 'unscheduled') return 'gray';
  return 'blue';
};

/**
 * Categorize frequency by type.
 */
export const categorizeFrequency = (frequency: string | undefined | null): 'daily' | 'weekly' | 'monthly' | 'custom' => {
  if (!frequency) return 'custom';

  const freqLower = frequency.toLowerCase();

  if (freqLower.includes('daily')) return 'daily';
  if (freqLower.includes('weekly')) return 'weekly';
  if (freqLower.includes('monthly')) return 'monthly';

  return 'custom';
};

/**
 * PHA-406 V2: Format a cadence object into a human-readable badge label.
 * E.g., "Daily 09:00", "Weekly Tue 12:00", "On-Demand"
 */
const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const format12h = (hour: number, minute: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  return `${h}:${String(minute).padStart(2, '0')} ${period}`;
};

export const formatCadenceBadge = (cadence: {
  cadence_type: string;
  cadence_day?: number | null;
  cadence_time_hour?: number | null;
  cadence_time_minute?: number | null;
} | null): string => {
  if (!cadence || cadence.cadence_type === 'unscheduled') return 'On-Demand';

  const time = format12h(cadence.cadence_time_hour ?? 9, cadence.cadence_time_minute ?? 0);

  if (cadence.cadence_type === 'daily') return `Daily ${time}`;
  if (cadence.cadence_type === 'weekly') {
    const day = DAY_NAMES_SHORT[cadence.cadence_day ?? 1];
    return `Weekly ${day} ${time}`;
  }
  return 'On-Demand';
};

export const getCadenceBadgeColor = (cadenceType: string | null | undefined): 'blue' | 'gray' => {
  if (!cadenceType || cadenceType === 'unscheduled') return 'gray';
  return 'blue';
};

/**
 * PHA-815 — SINGLE SOURCE OF TRUTH for a draft question's cadence.
 *
 * The backend is authoritative: survey generation and the draft-review endpoints
 * both use compute_question_cadence(), and each draft question is returned with a
 * `computed_cadence` field (a cron string, or 'unscheduled' for On-Demand). The
 * frontend MUST read that field instead of re-deriving the category/cadence from
 * the prompt — the old client-side categorizeQuestionFrontend/getQuestionCadenceFromTemplates
 * re-derivation drifted from the backend and caused PHA-670/672/736/754/812/815.
 *
 * A clinician's in-session edit (stored as a raw cron or 'unscheduled' in
 * question.frequency via the edit modal) takes precedence over the backend value.
 */
export const resolveQuestionCadence = (
  question: { frequency?: string | null; computed_cadence?: string | null }
): string => {
  const f = question.frequency;
  // Clinician-edited in this session: 'unscheduled' or a raw 5-part cron.
  if (f === 'unscheduled') return 'unscheduled';
  if (f && /^\d+\s+\d+\s/.test(f)) return f;
  // Otherwise the backend-computed authoritative cadence.
  return question.computed_cadence || 'unscheduled';
};

/**
 * Parse a cron string (or 'unscheduled') into the cadence object that
 * formatCadenceBadge / getCadenceBadgeColor consume. Returns null for On-Demand.
 */
export const cronToCadence = (
  cron: string | null | undefined
): { cadence_type: string; cadence_day?: number | null; cadence_time_hour?: number | null; cadence_time_minute?: number | null } | null => {
  if (!cron || cron === 'unscheduled') return null;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const minute = isNaN(parseInt(parts[0])) ? 0 : parseInt(parts[0]);
  const hour = isNaN(parseInt(parts[1])) ? 9 : parseInt(parts[1]);
  if (parts[4] === '*') {
    return { cadence_type: 'daily', cadence_day: null, cadence_time_hour: hour, cadence_time_minute: minute };
  }
  return {
    cadence_type: 'weekly',
    cadence_day: isNaN(parseInt(parts[4])) ? 1 : parseInt(parts[4]),
    cadence_time_hour: hour,
    cadence_time_minute: minute,
  };
};
